import {
  FunctionComponent,
  MutableRefObject,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import styles from './tooltipV2.module.css';

type TooltipProps = {
  tooltipContent: ReactNode;
  child?: ReactNode;
  style?: string; // Optional CSS class string for tooltip content
  triangle?: boolean;
  relativeContainerRef?: MutableRefObject<HTMLDivElement | null>;
};

const TooltipV2: FunctionComponent<TooltipProps> = ({
  tooltipContent,
  child,
  style,
  triangle,
  relativeContainerRef,
}) => {
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState<'top' | 'bottom'>('bottom');
  const marginBuffer = 8;

  useEffect(() => {
    const container = relativeContainerRef?.current;
    if (!container) return;

    function handleMouseMove(e: MouseEvent) {
      const tooltipEl = tooltipRef.current;
      if (!tooltipEl) return;

      const tooltipRect = tooltipEl.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      const mouseX = e.clientX;
      const mouseY = e.clientY;

      const isClippingBottom =
        mouseY + tooltipRect.height + marginBuffer > viewportHeight;
      const isClippingTop = mouseY - tooltipRect.height - marginBuffer < 0;

      if (isClippingBottom && !isClippingTop) {
        setPosition('top');
      } else if (isClippingTop && !isClippingBottom) {
        setPosition('bottom');
      } else if (!isClippingBottom) {
        setPosition('bottom');
      } else {
        setPosition('top');
      }

      setCursorPos({ x: mouseX, y: mouseY });
    }

    function handleMouseEnter() {
      setVisible(true);
    }
    function handleMouseLeave() {
      setVisible(false);
    }

    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseenter', handleMouseEnter);
    container.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseenter', handleMouseEnter);
      container.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [relativeContainerRef]);

  if (!visible) {
    return <>{child}</>;
  }

  // Position styles meeting your requested alignment
  const tooltipStyle: React.CSSProperties =
    position === 'bottom'
      ? {
          position: 'fixed',
          top: cursorPos.y,
          left: cursorPos.x,
          transform: 'translate(-50%, 0)', // tooltip top edge aligned with cursor
          pointerEvents: 'none',
          zIndex: 9999,
        }
      : {
          position: 'fixed',
          top: cursorPos.y,
          left: cursorPos.x,
          transform: 'translate(-50%, -100%)', // tooltip bottom edge aligned with cursor
          pointerEvents: 'none',
          zIndex: 9999,
        };

  return (
    <>
      {child}
      <span
        ref={tooltipRef}
        className={`${styles.tooltipContent} ${style ?? ''}`}
        style={tooltipStyle}
      >
        {tooltipContent}
        {triangle && <div className={styles.triangle}></div>}
      </span>
    </>
  );
};

export default TooltipV2;
